import { useRef, type ReactNode } from 'react';
import { KeyHint, OptionCard, useHotkeys, type KeyHintPart } from '../ui';
import { ART } from './art';
import { letterOf, type Answer } from './config';
import styles from './case.module.css';

export interface QuestionScreenProps<Id extends string> {
  title: string;
  /** A line under the title; the landing asks without one. */
  lede?: string;
  answers: readonly Answer<Id>[];
  picked: Id | null;
  /** Called with the picked card's title, for anything that grows out of that card. */
  onPick: (id: Id, title: HTMLElement) => void;
  hint: readonly KeyHintPart[];
  /** False on a screen that is on its way out: it shows, but takes no keys. */
  active: boolean;
  /** Something laid over the answers, for example the box to say it in one's own words. */
  over?: ReactNode;
}

const PER_ROW = 4;

/** One question, asked the way the Paper screens ask it: a big title, a line under it, and cards to pick. */
export function QuestionScreen<Id extends string>({ title, lede, answers, picked, onPick, hint, active, over }: QuestionScreenProps<Id>) {
  const grid = useRef<HTMLDivElement>(null);
  const rows = Math.ceil(answers.length / PER_ROW);

  const pick = (id: Id, index: number) => {
    const card = grid.current?.querySelectorAll<HTMLElement>('[data-option-title]')[index];
    if (card) onPick(id, card);
  };

  // A and a both pick the first card.
  useHotkeys(
    Object.fromEntries(
      answers.flatMap((answer, i) => {
        const pickThis = () => pick(answer.id, i);
        return [
          [letterOf(i), pickThis],
          [letterOf(i).toLowerCase(), pickThis],
        ];
      }),
    ),
    active && !over,
  );

  return (
    <section className={`${styles.question} ${rows > 1 ? styles.tall : ''}`}>
      <header className={styles.ask}>
        <h1 className={styles.title}>{title}</h1>
        {lede ? <p className={styles.lede}>{lede}</p> : null}
      </header>
      <div ref={grid} className={`${styles.answers} ${over ? styles.receded : ''}`}>
        {answers.map((answer, i) => (
          <OptionCard
            key={answer.id}
            visual={ART[answer.art]}
            title={answer.title}
            shortcut={letterOf(i)}
            selected={picked === answer.id}
            onSelect={() => pick(answer.id, i)}
          />
        ))}
        {over}
      </div>
      <KeyHint parts={hint} />
    </section>
  );
}
